/**
 * HUD - Futuristic Head-Up Display
 * Real-time telemetry and controls for Sol Invictus visualization
 * MOBILE RESPONSIVE - Simplified UI on small screens
 */

import { useEffect, useState } from 'react';
import { useStore, CameraMode, CameraTarget } from '../../store/useStore';

// Hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return isMobile;
}
import {
  EAHP_DATA,
  ARCHITECTURE_COMPARISON,
  IMPLEMENTATION_ROADMAP,
  ORBITAL_ENVIRONMENT
} from '../../data/scientificData';

export function HUD() {
  const {
    telemetry,
    updateTelemetry,
    view,
    toggleLasers,
    toggleOrbits,
    toggleLabels,
    toggleLDRParticles,
    toggleEAHPSwarm,
    showInfoPanel,
    toggleInfoPanel,
    activeTab,
    setActiveTab,
    isPaused,
    togglePause,
    animationSpeed,
    setAnimationSpeed,
    setCameraMode,
    setCameraTarget,
    nextShip,
    prevShip,
    isClipMode,
    startClipMode,
  } = useStore();

  // Simulate telemetry updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPaused) {
        updateTelemetry({
          swarmTemperature: 880 + Math.sin(Date.now() / 5000) * 20,
          powerOutput: telemetry.powerOutput + (Math.random() - 0.5) * 0.01,
          systemEfficiency: 30 + Math.sin(Date.now() / 3000) * 2,
          laserTransmissionRate: telemetry.laserTransmissionRate + (Math.random() - 0.5) * 0.001,
        });
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isPaused, telemetry.powerOutput, telemetry.laserTransmissionRate]);

  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Hide HUD during clip mode for clean cinematic experience
  if (isClipMode) return null;

  // MOBILE: Simplified HUD
  if (isMobile) {
    return (
      <div className="fixed inset-0 pointer-events-none z-50">
        {/* Mobile top bar */}
        <div className="absolute top-0 left-0 right-0 p-3 flex justify-between items-center">
          <div className="pointer-events-auto">
            <h1 className="text-lg font-bold text-white tracking-wider">
              SPACE<span className="text-gray-400">X</span>
              <span className="text-orange-500 ml-2">SOL INVICTUS</span>
            </h1>
          </div>

          {/* Menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="pointer-events-auto bg-black/70 border border-cyan-500/50 rounded px-3 py-2 text-cyan-400 text-sm"
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* Mobile menu overlay */}
        {mobileMenuOpen && (
          <div className="absolute top-14 right-3 w-48 bg-black/90 border border-cyan-500/30 rounded-lg p-3 pointer-events-auto">
            <div className="space-y-2">
              <button
                onClick={() => { startClipMode(); setMobileMenuOpen(false); }}
                className="w-full bg-orange-500 text-black py-2 rounded font-bold text-sm"
              >
                ▶ PLAY CLIP
              </button>
              <ToggleButton label="Lasers" active={view.showLasers} onClick={toggleLasers} />
              <ToggleButton label="Swarm" active={view.showEAHPSwarm} onClick={toggleEAHPSwarm} />
              <button
                onClick={togglePause}
                className="w-full bg-gray-700 text-white py-1 rounded text-sm"
              >
                {isPaused ? '▶ PLAY' : '⏸ PAUSE'}
              </button>
            </div>
          </div>
        )}

        {/* Mobile bottom hint */}
        <div className="absolute bottom-4 left-0 right-0 text-center pointer-events-none">
          <p className="text-gray-500 text-xs">Pinch to zoom • Drag to rotate</p>
        </div>
      </div>
    );
  }

  // DESKTOP: Full HUD
  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {/* Top bar - Title and status */}
      <div className="absolute top-0 left-0 right-0 p-4">
        <div className="flex justify-between items-start">
          {/* Title */}
          <div className="pointer-events-auto">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white tracking-wider">
                SPACE<span className="text-gray-400">X</span>
              </h1>
              <div className="h-6 w-px bg-gray-600" />
              <h1 className="text-2xl font-bold text-orange-500 tracking-wider">
                SOL INVICTUS
              </h1>
            </div>
            <p className="text-xs text-gray-400 font-mono">
              STARSHIP HELIOS-PROXIMAL ENERGY ARCHITECTURE
            </p>
          </div>

          {/* System status */}
          <div className="bg-black/70 border border-green-500/50 rounded px-4 py-2 pointer-events-auto">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-green-500 text-sm font-mono">SYSTEM ACTIVE</span>
            </div>
          </div>
        </div>
      </div>

      {/* Left panel - Telemetry */}
      <div className="absolute left-4 top-24 w-72 pointer-events-auto">
        <div className="bg-black/80 border border-orange-500/30 rounded-lg overflow-hidden">
          <div className="bg-orange-500/20 px-4 py-2 border-b border-orange-500/30">
            <h2 className="text-orange-500 font-mono text-sm tracking-wider">
              TELEMETRY
            </h2>
          </div>

          <div className="p-4 space-y-4">
            {/* Temperature */}
            <TelemetryItem
              label="SWARM TEMP"
              value={telemetry.swarmTemperature.toFixed(0)}
              unit="K"
              color="text-red-400"
              barColor="bg-red-500"
              percent={(telemetry.swarmTemperature - 800) / 4}
            />

            {/* Power Output */}
            <TelemetryItem
              label="POWER OUTPUT"
              value={telemetry.powerOutput.toFixed(2)}
              unit="PW"
              color="text-yellow-400"
              barColor="bg-yellow-500"
              percent={telemetry.powerOutput * 10}
            />

            {/* Efficiency */}
            <TelemetryItem
              label="DSPL EFFICIENCY"
              value={telemetry.systemEfficiency.toFixed(1)}
              unit="%"
              color="text-green-400"
              barColor="bg-green-500"
              percent={telemetry.systemEfficiency}
            />

            {/* Transmission */}
            <TelemetryItem
              label="LASER TX RATE"
              value={telemetry.laserTransmissionRate.toFixed(3)}
              unit="TW"
              color="text-cyan-400"
              barColor="bg-cyan-500"
              percent={telemetry.laserTransmissionRate * 1000}
            />

            {/* Active units */}
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400 font-mono">ACTIVE UNITS</span>
              <span className="text-white font-mono">{telemetry.activeSwarmUnits.toLocaleString()}</span>
            </div>

            {/* Mercury relay status */}
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400 font-mono">MERCURY RELAY</span>
              <span className={`font-mono ${
                telemetry.mercuryRelayStatus === 'active' ? 'text-green-400' :
                telemetry.mercuryRelayStatus === 'standby' ? 'text-yellow-400' : 'text-red-400'
              }`}>
                {telemetry.mercuryRelayStatus.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - Controls */}
      <div className="absolute right-4 top-24 w-64 pointer-events-auto">
        <div className="bg-black/80 border border-cyan-500/30 rounded-lg overflow-hidden">
          <div className="bg-cyan-500/20 px-4 py-2 border-b border-cyan-500/30">
            <h2 className="text-cyan-500 font-mono text-sm tracking-wider">
              CONTROLS
            </h2>
          </div>

          <div className="p-4 space-y-3">
            <ToggleButton
              label="Laser Beams"
              active={view.showLasers}
              onClick={toggleLasers}
            />
            <ToggleButton
              label="Orbit Paths"
              active={view.showOrbits}
              onClick={toggleOrbits}
            />
            <ToggleButton
              label="Labels"
              active={view.showLabels}
              onClick={toggleLabels}
            />
            <ToggleButton
              label="LDR Particles"
              active={view.showLDRParticles}
              onClick={toggleLDRParticles}
            />
            <ToggleButton
              label="EAHP Swarm"
              active={view.showEAHPSwarm}
              onClick={toggleEAHPSwarm}
            />

            <div className="border-t border-gray-700 pt-3 mt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm font-mono">SPEED</span>
                <span className="text-white text-sm font-mono">{animationSpeed.toFixed(1)}x</span>
              </div>
              <input
                type="range"
                min="0.1"
                max="3"
                step="0.1"
                value={animationSpeed}
                onChange={(e) => setAnimationSpeed(parseFloat(e.target.value))}
                className="w-full accent-cyan-500"
              />
            </div>

            <button
              onClick={togglePause}
              className={`w-full py-2 rounded font-mono text-sm transition ${
                isPaused
                  ? 'bg-green-600 hover:bg-green-500 text-white'
                  : 'bg-red-600 hover:bg-red-500 text-white'
              }`}
            >
              {isPaused ? '▶ RESUME' : '⏸ PAUSE'}
            </button>
          </div>
        </div>

        {/* Camera Controls Panel */}
        <div className="bg-black/80 border border-purple-500/30 rounded-lg overflow-hidden mt-4">
          <div className="bg-purple-500/20 px-4 py-2 border-b border-purple-500/30">
            <h2 className="text-purple-500 font-mono text-sm tracking-wider">
              CAMERA
            </h2>
          </div>

          <div className="p-4 space-y-3">
            {/* PLAY CLIP Button - Big and prominent */}
            <button
              onClick={startClipMode}
              disabled={isClipMode}
              className="w-full py-3 rounded-lg font-mono text-sm transition bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="text-lg">▶</span>
              <span>PLAY CLIP</span>
              <span className="text-xs opacity-70">2:45</span>
            </button>

            {/* Camera Mode Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <CameraModeButton
                mode="free"
                label="FREE"
                icon="🌐"
                currentMode={view.cameraMode}
                onClick={() => setCameraMode('free')}
              />
              <CameraModeButton
                mode="cinematic"
                label="CINE"
                icon="🎬"
                currentMode={view.cameraMode}
                onClick={() => setCameraMode('cinematic')}
              />
              <CameraModeButton
                mode="pov"
                label="POV"
                icon="👁"
                currentMode={view.cameraMode}
                onClick={() => {
                  setCameraMode('pov');
                  setCameraTarget('starship');
                }}
              />
              <CameraModeButton
                mode="chase"
                label="CHASE"
                icon="🚀"
                currentMode={view.cameraMode}
                onClick={() => {
                  setCameraMode('chase');
                  setCameraTarget('starship');
                }}
              />
            </div>

            {/* Focus Mode Targets */}
            <div className="border-t border-gray-700 pt-3">
              <span className="text-gray-400 text-xs font-mono mb-2 block">FOCUS ON</span>
              <div className="grid grid-cols-3 gap-1">
                <FocusTargetButton
                  target="sun"
                  label="SOL"
                  currentTarget={view.cameraTarget}
                  currentMode={view.cameraMode}
                  onClick={() => {
                    setCameraMode('focus');
                    setCameraTarget('sun');
                  }}
                />
                <FocusTargetButton
                  target="mercury"
                  label="MERC"
                  currentTarget={view.cameraTarget}
                  currentMode={view.cameraMode}
                  onClick={() => {
                    setCameraMode('focus');
                    setCameraTarget('mercury');
                  }}
                />
                <FocusTargetButton
                  target="earth"
                  label="EARTH"
                  currentTarget={view.cameraTarget}
                  currentMode={view.cameraMode}
                  onClick={() => {
                    setCameraMode('focus');
                    setCameraTarget('earth');
                  }}
                />
                <FocusTargetButton
                  target="eahp"
                  label="EAHP"
                  currentTarget={view.cameraTarget}
                  currentMode={view.cameraMode}
                  onClick={() => {
                    setCameraMode('focus');
                    setCameraTarget('eahp');
                  }}
                />
                <FocusTargetButton
                  target="geoReceiver"
                  label="GEO"
                  currentTarget={view.cameraTarget}
                  currentMode={view.cameraMode}
                  onClick={() => {
                    setCameraMode('focus');
                    setCameraTarget('geoReceiver');
                  }}
                />
                <FocusTargetButton
                  target="starship"
                  label="SHIP"
                  currentTarget={view.cameraTarget}
                  currentMode={view.cameraMode}
                  onClick={() => {
                    setCameraMode('focus');
                    setCameraTarget('starship');
                  }}
                />
              </div>
            </div>

            {/* Ship Selection (for POV/Chase/Focus on starship) */}
            {(view.cameraMode === 'pov' || view.cameraMode === 'chase' ||
              (view.cameraMode === 'focus' && view.cameraTarget === 'starship')) && (
              <div className="border-t border-gray-700 pt-3">
                <span className="text-gray-400 text-xs font-mono mb-2 block">STARSHIP</span>
                <div className="flex items-center justify-between">
                  <button
                    onClick={prevShip}
                    className="px-3 py-1 bg-gray-800 hover:bg-gray-700 text-white rounded font-mono text-sm"
                  >
                    ◀
                  </button>
                  <span className="text-purple-400 font-mono">
                    SHIP {view.followShipIndex + 1}
                  </span>
                  <button
                    onClick={nextShip}
                    className="px-3 py-1 bg-gray-800 hover:bg-gray-700 text-white rounded font-mono text-sm"
                  >
                    ▶
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom panel - Info tabs */}
      {showInfoPanel && (
        <div className="absolute bottom-4 left-4 right-4 pointer-events-auto">
          <div className="bg-black/80 border border-gray-700 rounded-lg overflow-hidden max-w-4xl mx-auto">
            {/* Tab buttons */}
            <div className="flex border-b border-gray-700">
              {(['overview', 'eahp', 'technology', 'comparison'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 px-4 py-2 font-mono text-sm transition ${
                    activeTab === tab
                      ? 'bg-orange-500/20 text-orange-500 border-b-2 border-orange-500'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  {tab.toUpperCase()}
                </button>
              ))}
              <button
                onClick={toggleInfoPanel}
                className="px-4 py-2 text-gray-400 hover:text-white hover:bg-gray-800"
              >
                ✕
              </button>
            </div>

            {/* Tab content */}
            <div className="p-4 max-h-48 overflow-y-auto">
              {activeTab === 'overview' && <OverviewTab />}
              {activeTab === 'eahp' && <EAHPTab />}
              {activeTab === 'technology' && <TechnologyTab />}
              {activeTab === 'comparison' && <ComparisonTab />}
            </div>
          </div>
        </div>
      )}

      {/* Toggle info panel button (when hidden) */}
      {!showInfoPanel && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-auto">
          <button
            onClick={toggleInfoPanel}
            className="bg-black/80 border border-orange-500/50 rounded px-6 py-2 text-orange-500 font-mono text-sm hover:bg-orange-500/20 transition"
          >
            SHOW INFO PANEL
          </button>
        </div>
      )}

      {/* Current camera mode indicator */}
      <div className="absolute bottom-4 right-4 text-xs font-mono pointer-events-none">
        <div className="text-purple-500 mb-1">
          CAM: {view.cameraMode.toUpperCase()}
          {view.cameraTarget && ` → ${view.cameraTarget.toUpperCase()}`}
        </div>
        <div className="text-gray-600">
          [SCROLL: ZOOM] [DRAG: ROTATE]
        </div>
      </div>
    </div>
  );
}

// Telemetry item component
function TelemetryItem({
  label,
  value,
  unit,
  color,
  barColor,
  percent
}: {
  label: string;
  value: string;
  unit: string;
  color: string;
  barColor: string;
  percent: number;
}) {
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-gray-400 text-xs font-mono">{label}</span>
        <span className={`${color} font-mono text-lg`}>
          {value}<span className="text-xs ml-1">{unit}</span>
        </span>
      </div>
      <div className="h-1 bg-gray-800 rounded overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-300`}
          style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
        />
      </div>
    </div>
  );
}

// Toggle button component
function ToggleButton({
  label,
  active,
  onClick
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm font-mono transition ${
        active
          ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
          : 'bg-gray-800 text-gray-500 border border-gray-700 hover:border-gray-600'
      }`}
    >
      <span>{label}</span>
      <span>{active ? 'ON' : 'OFF'}</span>
    </button>
  );
}

// Tab content components
function OverviewTab() {
  return (
    <div className="grid grid-cols-2 gap-4 text-sm">
      <div>
        <h3 className="text-orange-500 font-mono mb-2">SOL INVICTUS CONCEPT</h3>
        <p className="text-gray-300">
          A Dyson Swarm subset at 0.1 AU (15 million km) from the Sun, where solar irradiance
          is 100x stronger than at Earth orbit. Direct Solar-Pumped Lasers (DSPL) convert
          sunlight directly to coherent beams, transmitted via Mercury relay to Earth.
        </p>
      </div>
      <div>
        <h3 className="text-cyan-500 font-mono mb-2">KEY METRICS</h3>
        <ul className="text-gray-300 space-y-1">
          <li>• Irradiance: 136,100 W/m² (vs 1,361 at Earth)</li>
          <li>• Target capacity: 10+ Terawatts</li>
          <li>• Mercury ISRU manufacturing base</li>
          <li>• Path to Kardashev Type I civilization</li>
        </ul>
      </div>
    </div>
  );
}

function EAHPTab() {
  return (
    <div className="grid grid-cols-3 gap-4 text-sm">
      <div>
        <h3 className="text-orange-500 font-mono mb-2">SWARM SPECS</h3>
        <ul className="text-gray-300 space-y-1">
          <li>Distance: {EAHP_DATA.orbital.distance.value} {EAHP_DATA.orbital.distance.unit}</li>
          <li>Type: {EAHP_DATA.orbital.type}</li>
          <li>Units: {EAHP_DATA.singleUnit.aperture.value}m aperture</li>
          <li>Output/unit: {EAHP_DATA.singleUnit.powerOutput.value} MW</li>
        </ul>
      </div>
      <div>
        <h3 className="text-green-500 font-mono mb-2">ADVANTAGES</h3>
        <ul className="text-gray-300 space-y-1">
          {EAHP_DATA.advantages.map((adv, i) => (
            <li key={i}>• {adv}</li>
          ))}
        </ul>
      </div>
      <div>
        <h3 className="text-red-500 font-mono mb-2">CHALLENGES</h3>
        <ul className="text-gray-300 space-y-1">
          {EAHP_DATA.challenges.map((ch, i) => (
            <li key={i}>• {ch}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function TechnologyTab() {
  return (
    <div className="grid grid-cols-2 gap-4 text-sm">
      <div>
        <h3 className="text-purple-500 font-mono mb-2">DSPL - SOLAR LASER</h3>
        <p className="text-gray-300 mb-2">
          Direct Solar-Pumped Lasers eliminate electricity conversion.
          Nd:YAG crystal co-doped with Cr/Ce directly converts sunlight to 1064nm laser.
        </p>
        <p className="text-gray-400 text-xs">
          Efficiency: 7% current → 15% theoretical
        </p>
      </div>
      <div>
        <h3 className="text-orange-500 font-mono mb-2">LDR - HEAT REJECTION</h3>
        <p className="text-gray-300 mb-2">
          Liquid Droplet Radiators eject tin droplets into space.
          They radiate heat during ballistic flight and are recollected.
        </p>
        <p className="text-gray-400 text-xs">
          5-10x lighter than conventional radiators
        </p>
      </div>
    </div>
  );
}

function ComparisonTab() {
  const { architectures } = ARCHITECTURE_COMPARISON;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-400">
            <th className="text-left py-1">Parameter</th>
            <th className="text-center py-1 text-blue-400">Terrestrial</th>
            <th className="text-center py-1 text-yellow-400">GEO SBSP</th>
            <th className="text-center py-1 text-orange-400">EAHP</th>
          </tr>
        </thead>
        <tbody className="text-gray-300">
          <tr>
            <td className="py-1">Power Density</td>
            <td className="text-center">{architectures.terrestrialSolar.powerDensity.value} kW/m²</td>
            <td className="text-center">{architectures.geoSBSP.powerDensity.value} kW/m²</td>
            <td className="text-center text-orange-400">{architectures.eahp.powerDensity.value} kW/m²</td>
          </tr>
          <tr>
            <td className="py-1">LCOE ($/kWh)</td>
            <td className="text-center">${architectures.terrestrialSolar.longTermCost.lcoe}</td>
            <td className="text-center">${architectures.geoSBSP.longTermCost.lcoe}</td>
            <td className="text-center text-green-400">${architectures.eahp.longTermCost.lcoe}*</td>
          </tr>
          <tr>
            <td className="py-1">Mass Origin</td>
            <td className="text-center">{architectures.terrestrialSolar.massOrigin}</td>
            <td className="text-center">{architectures.geoSBSP.massOrigin}</td>
            <td className="text-center">{architectures.eahp.massOrigin}</td>
          </tr>
          <tr>
            <td className="py-1">Scalability</td>
            <td className="text-center">{architectures.terrestrialSolar.scalability}</td>
            <td className="text-center">{architectures.geoSBSP.scalability}</td>
            <td className="text-center text-green-400">{architectures.eahp.scalability}</td>
          </tr>
        </tbody>
      </table>
      <p className="text-gray-500 text-xs mt-2">* Post-amortization estimate</p>
    </div>
  );
}

// Camera mode button component
function CameraModeButton({
  mode,
  label,
  icon,
  currentMode,
  onClick
}: {
  mode: CameraMode;
  label: string;
  icon: string;
  currentMode: CameraMode;
  onClick: () => void;
}) {
  const isActive = mode === currentMode;

  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-center gap-1 px-2 py-2 rounded text-xs font-mono transition ${
        isActive
          ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50'
          : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-purple-500/30 hover:text-purple-400'
      }`}
    >
      <span>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// Focus target button component
function FocusTargetButton({
  target,
  label,
  currentTarget,
  currentMode,
  onClick
}: {
  target: CameraTarget;
  label: string;
  currentTarget: CameraTarget;
  currentMode: CameraMode;
  onClick: () => void;
}) {
  const isActive = currentMode === 'focus' && target === currentTarget;

  return (
    <button
      onClick={onClick}
      className={`px-2 py-1 rounded text-xs font-mono transition ${
        isActive
          ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50'
          : 'bg-gray-800/50 text-gray-500 border border-gray-700/50 hover:border-purple-500/30 hover:text-purple-400'
      }`}
    >
      {label}
    </button>
  );
}
